import { assertSuccess, binToHex, decodeTransaction as decodeTransactionLibAuth, hexToBin, isPayToPublicKey, lockingBytecodeToAddressContents, lockingBytecodeToCashAddress, publicKeyToP2pkhCashAddress, } from "@bitauth/libauth";
import { bchParam } from "../chain.js";
import { NetworkType, prefixFromNetworkMap } from "../enum.js";
import { getNetworkProvider } from "../network/default.js";
import { getTransactionHash } from "../util/transaction.js";
/**
 * Class with various wallet utilities.
 */
export class Util {
    /**
     * Initializes a wallet Util class.
     *
     * @param network     The network type to use. Defaults to mainnet.
     */
    constructor(network = NetworkType.Mainnet) {
        this.network = network;
        this.provider = getNetworkProvider(network);
    }
    async getTransactionHash(rawTransactionHex) {
        return getTransactionHash(rawTransactionHex);
    }
    static async getTransactionHash(rawTransactionHex) {
        return getTransactionHash(rawTransactionHex);
    }
    async decodeTransaction(transactionHashOrHex, loadInputValues = false) {
        let transactionHex;
        let transactionBin;
        let txHash;
        // raw transaction
        if (transactionHashOrHex.length > 64) {
            txHash = await this.getTransactionHash(transactionHashOrHex);
            transactionBin = hexToBin(transactionHashOrHex);
            transactionHex = transactionHashOrHex;
        }
        else {
            // tx hash, look up the raw transaction
            txHash = transactionHashOrHex;
            transactionHex = await this.provider.getRawTransaction(txHash);
            transactionBin = hexToBin(transactionHex);
        }
        const result = decodeTransactionLibAuth(transactionBin);
        if (typeof result === "string") {
            throw Error(result);
        }
        const transaction = this.mapToElectrumRawTransaction(result, txHash, transactionHex);
        if (loadInputValues) {
            // get unique transaction hashes
            const hashes = [...new Set(transaction.vin.map((val) => val.txid))];
            const transactions = await Promise.all(hashes.map((hash) => this.decodeTransaction(hash, false)));
            const transactionMap = new Map();
            transactions.forEach((val) => transactionMap.set(val.hash, val));
            const enrichedVin = transaction.vin.map((input) => {
                const output = transactionMap
                    .get(input.txid)
                    .vout.find((val) => val.n === input.vout);
                return { ...input, ...output };
            });
            return { ...transaction, vin: enrichedVin };
        }
        return transaction;
    }
    mapToElectrumRawTransaction(transaction, txHash, txHex) {
        return {
            blockhash: "",
            blocktime: 0,
            confirmations: 0,
            time: 0,
            hash: txHash,
            hex: txHex,
            txid: txHash,
            locktime: transaction.locktime,
            version: transaction.version,
            size: txHex.length / 2,
            vin: transaction.inputs.map((input) => ({
                scriptSig: {
                    asm: "",
                    hex: binToHex(input.unlockingBytecode),
                },
                sequence: input.sequenceNumber,
                txid: binToHex(input.outpointTransactionHash),
                vout: input.outpointIndex,
            })),
            vout: transaction.outputs.map((output, index) => ({
                n: index,
                scriptPubKey: {
                    addresses: [
                        isPayToPublicKey(output.lockingBytecode)
                            ? publicKeyToP2pkhCashAddress({
                                publicKey: lockingBytecodeToAddressContents(output.lockingBytecode).payload,
                                prefix: prefixFromNetworkMap[this.network],
                            }).address
                            : assertSuccess(lockingBytecodeToCashAddress({
                                bytecode: output.lockingBytecode,
                                prefix: prefixFromNetworkMap[this.network],
                            })).address,
                    ],
                    asm: "",
                    hex: binToHex(output.lockingBytecode),
                    reqSigs: 1,
                    type: "",
                },
                value: Number(output.valueSatoshis) / Number(bchParam.subUnits),
            })),
        };
    }
    static async decodeTransaction(transactionHashOrHex, loadInputValues = false, network) {
        if (loadInputValues) {
            return new this(network).decodeTransaction(transactionHashOrHex, true);
        }
        return new this(network).decodeTransaction(transactionHashOrHex);
    }
}
//# sourceMappingURL=Util.js.map